/**
 * Relyxi custom add-to-cart.
 * Same request as Horizon's native product form (POST /cart/add.js + sections), then the
 * theme's own CartLinesUpdateEvent pipeline morphs the cart drawer and updates the bubble.
 *
 * Safety nets (a sale is never lost):
 *  - if the request with `sections` fails for a non-cart reason, it is retried without them;
 *  - if the cart event pipeline is unavailable, the visitor is sent to /cart after adding;
 *  - any unexpected failure falls back to the native form POST (/cart/add -> /cart).
 * Real cart errors (sold out, quantity limit) are shown under the button.
 *
 * The button markup (.rx-btn-label + .rx-btn-price) is saved as HTML and restored as HTML,
 * so the Weglot-excluded price span and the translated label survive a click.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const FORM_SELECTOR = 'form[data-rx-atc], .rxm form[action$="/cart/add"]';
const LOG = '[Relyxi ATC]';

const themeRoutes = () => (typeof Theme !== 'undefined' && Theme.routes) || {};
const root = () => window.Shopify?.routes?.root || '/';
const addUrl = () => themeRoutes().cart_add_url || `${root()}cart/add.js`;
const cartUrl = () => themeRoutes().cart_url || `${root()}cart`;

const busyForms = new WeakSet();

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return [...ids];
}

async function postAdd(formData) {
  const response = await fetch(addUrl(), {
    method: 'POST',
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: formData,
    credentials: 'same-origin',
  });
  let data = {};
  try {
    data = await response.json();
  } catch (_) {}
  return { response, data };
}

async function fetchCart() {
  const response = await fetch(`${cartUrl()}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
  return response.json();
}

async function openCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) {
    window.location.href = cartUrl();
    return;
  }
  await customElements.whenDefined('theme-drawer');
  /** @type {any} */ (drawer).open();
}

function showError(form, message) {
  let el = form.querySelector('.rx-atc-error');
  if (!el) {
    el = document.createElement('p');
    el.className = 'rx-atc-error';
    el.setAttribute('role', 'alert');
    form.appendChild(el);
  }
  el.textContent = message;
  clearTimeout(Number(el.dataset.timer || 0));
  el.dataset.timer = String(setTimeout(() => el.remove(), 7000));
}

function nativeSubmit(form) {
  HTMLFormElement.prototype.submit.call(form);
}

function createCartEvent(form, variantId, quantity) {
  try {
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise,
      })
    );
    return deferred;
  } catch (error) {
    console.warn(LOG, 'cart event pipeline unavailable', error);
    return null;
  }
}

/** @param {HTMLFormElement} form */
async function addToCart(form) {
  const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
  if (!button || button.disabled || busyForms.has(form)) return;

  const variantId = String(new FormData(form).get('id') || '');
  const quantity = Number(new FormData(form).get('quantity')) || 1;
  if (!variantId) {
    console.error(LOG, 'variant id missing, using native submit');
    nativeSubmit(form);
    return;
  }

  busyForms.add(form);
  const labelHtml = button.innerHTML;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  button.textContent = 'ADDING…';
  form.querySelector('.rx-atc-error')?.remove();

  let success = false;
  let added = false;
  let deferred = null;

  const restore = (delay) =>
    setTimeout(() => {
      button.innerHTML = labelHtml;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      busyForms.delete(form);
    }, delay);

  try {
    const sectionIds = cartSectionIds();
    const withSections = new FormData(form);
    if (sectionIds.length) withSections.append('sections', sectionIds.join(','));

    deferred = createCartEvent(form, variantId, quantity);

    let { response, data } = await postAdd(withSections);

    // Cart errors (sold out, limits) come back as 422 with a description.
    const isCartError = response.status === 422 || data.status === 422;

    if (!response.ok && !isCartError && sectionIds.length) {
      console.warn(LOG, `add with sections failed (${response.status}), retrying without sections`, data);
      ({ response, data } = await postAdd(new FormData(form)));
    }

    if (!response.ok || data.status) {
      const message = data.description || data.message || `Add to cart failed (${response.status})`;
      console.error(LOG, message, { status: response.status, data });

      if (response.status === 422 || data.status === 422) {
        try {
          form.dispatchEvent(
            new CartErrorEvent({
              error: message,
              code: 'INVALID',
              detail: { description: data.description, errors: data.errors },
            })
          );
        } catch (_) {}
        const cart = await fetchCart().catch(() => null);
        if (deferred && cart) {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, itemCount: quantity, source: 'relyxi-atc' },
          });
        }
        deferred?.reject(new Error(message));
        showError(form, message);
        button.textContent = 'TRY AGAIN';
        restore(1300);
        return;
      }

      // Unexpected server/network problem: let Shopify handle it natively.
      deferred?.reject(new Error(message));
      nativeSubmit(form);
      return;
    }

    added = true;
    const cart = await fetchCart();
    if (deferred) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: quantity,
          sections: data.sections,
          source: 'relyxi-atc',
          didError: false,
        },
      });
    }

    success = true;
    button.textContent = 'ADDED ✓';

    if (deferred) {
      await openCartDrawer();
    } else {
      window.location.href = cartUrl();
    }
    restore(700);
  } catch (error) {
    console.error(LOG, error);
    deferred?.reject(error);
    if (success) {
      restore(700);
      return;
    }
    if (added) {
      // Item is already in the cart; only the drawer refresh failed.
      window.location.href = cartUrl();
      return;
    }
    // Item is not in the cart: never lose the sale, fall back to the native POST.
    nativeSubmit(form);
  }
}

if (!window.__relyxiAtcBound) {
  window.__relyxiAtcBound = true;

  document.addEventListener('submit', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLFormElement) || !target.matches(FORM_SELECTOR)) return;
    event.preventDefault();
    addToCart(target);
  });
}
